/**
 * API Utility Functions
 *
 * WordPress REST API utilities for chatbot admin
 *
 * @package WooAIChatbot
 */
import { parseApiError, logError, createNetworkError } from './api/errors';
const getApiConfig = () => {
    const globals = window.wooAiChatbot ?? window.wooAIChatbot;
    // Use absolute URL with origin to avoid path resolution issues
    // When on /wp-admin/ pages, relative /wp-json resolves incorrectly
    const origin = window.location.origin;
    return {
        restUrl: `${origin}/wp-json`,
        nonce: globals?.nonce || '',
    };
};
const fetchAPI = async (endpoint, options = {}) => {
    const { restUrl, nonce } = getApiConfig();
    const url = `${restUrl}${endpoint}`;
    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': nonce,
                ...options.headers,
            },
        });
        if (!response.ok) {
            // Parse error response
            let errorData;
            try {
                errorData = await response.json();
            }
            catch {
                errorData = { message: 'Request failed', code: 'request_failed' };
            }
            // Create and log structured error
            const apiError = parseApiError({
                ...errorData,
                data: { status: response.status },
            });
            logError(apiError, { endpoint, method: options.method || 'GET' });
            throw apiError;
        }
        return response.json();
    }
    catch (error) {
        // Handle network errors and other exceptions
        if (error instanceof Error && error.name === 'ApiError') {
            // Already logged and structured, re-throw
            throw error;
        }
        // Parse and log unexpected errors
        const apiError = error instanceof Error
            ? parseApiError(error)
            : createNetworkError('Network request failed');
        logError(apiError, { endpoint, method: options.method || 'GET' });
        throw apiError;
    }
};
/**
 * Playbook API Functions
 */
export const playbooksApi = {
    /**
     * Get all playbooks
     */
    getAll: async () => {
        return fetchAPI('/woo-ai/v1/playbooks');
    },
    /**
     * Get a single playbook by ID
     */
    getOne: async (id) => {
        return fetchAPI(`/woo-ai/v1/playbooks/${id}`);
    },
    /**
     * Create a new playbook
     */
    create: async (data) => {
        return fetchAPI('/woo-ai/v1/playbooks', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
    /**
     * Update an existing playbook
     */
    update: async (id, data) => {
        return fetchAPI(`/woo-ai/v1/playbooks/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },
    /**
     * Delete a playbook
     */
    delete: async (id) => {
        return fetchAPI(`/woo-ai/v1/playbooks/${id}`, {
            method: 'DELETE',
        });
    },
    /**
     * Duplicate a playbook
     */
    duplicate: async (id) => {
        return fetchAPI(`/woo-ai/v1/playbooks/${id}/duplicate`, {
            method: 'POST',
        });
    },
    /**
     * Toggle playbook active status
     */
    toggle: async (id) => {
        return fetchAPI(`/woo-ai/v1/playbooks/${id}/toggle`, {
            method: 'POST',
        });
    },
};
/**
 * Analytics API Functions
 */
export const analyticsApi = {
    /**
     * Get playbook usage statistics
     */
    getPlaybookStats: async (id) => {
        return fetchAPI(`/woo-ai/v1/analytics/playbooks/${id}`);
    },
};
/**
 * Promotions API Functions
 */
export const promotionsApi = {
    /**
     * Get all promotions
     */
    getAll: async () => {
        return fetchAPI('/woo-ai/v1/promotions');
    },
    /**
     * Get a single promotion by ID
     */
    getOne: async (id) => {
        return fetchAPI(`/woo-ai/v1/promotions/${id}`);
    },
    /**
     * Create a new promotion
     */
    create: async (data) => {
        return fetchAPI('/woo-ai/v1/promotions', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
    /**
     * Update an existing promotion
     */
    update: async (id, data) => {
        return fetchAPI(`/woo-ai/v1/promotions/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },
    /**
     * Delete a promotion
     */
    delete: async (id) => {
        return fetchAPI(`/woo-ai/v1/promotions/${id}`, {
            method: 'DELETE',
        });
    },
    /**
     * Toggle promotion active status
     */
    toggle: async (id) => {
        return fetchAPI(`/woo-ai/v1/promotions/${id}/toggle`, {
            method: 'POST',
        });
    },
};
/**
 * Products API Functions
 */
export const productsApi = {
    /**
     * Search products with optional filters and pagination
     */
    search: async (params = {}) => {
        const urlParams = new URLSearchParams();
        if (params.search)
            urlParams.append('search', params.search);
        if (params.category)
            urlParams.append('category', params.category);
        if (params.tag)
            urlParams.append('tag', params.tag);
        if (params.page)
            urlParams.append('page', params.page.toString());
        if (params.perPage)
            urlParams.append('per_page', params.perPage.toString());
        const result = await fetchAPI(`/woo-ai/v1/products/search?${urlParams.toString()}`);
        return {
            products: result.products,
            total: result.total,
            pages: result.pages,
            page: result.page,
            perPage: result.per_page,
        };
    },
    /**
     * Get products by IDs
     */
    getByIds: async (ids) => {
        if (ids.length === 0)
            return [];
        return fetchAPI('/woo-ai/v1/products/by-ids', {
            method: 'POST',
            body: JSON.stringify({ ids }),
        });
    },
};
/**
 * Categories API Functions
 */
export const categoriesApi = {
    /**
     * Get all product categories
     */
    getAll: async () => {
        return fetchAPI('/woo-ai/v1/categories');
    },
};
/**
 * Tags API Functions
 */
export const tagsApi = {
    /**
     * Get all product tags
     */
    getAll: async () => {
        return fetchAPI('/woo-ai/v1/tags');
    },
};
/**
 * RAG Exclusions API Functions
 */
export const ragExclusionsApi = {
    /**
     * Get list of excluded product IDs
     */
    getAll: async () => {
        return fetchAPI('/woo-ai/v1/rag/excluded-products');
    },
    /**
     * Add products to exclusion list
     */
    add: async (productIds) => {
        return fetchAPI('/woo-ai/v1/rag/excluded-products', {
            method: 'POST',
            body: JSON.stringify({ product_ids: productIds }),
        });
    },
    /**
     * Remove a product from exclusion list (restore to index)
     */
    remove: async (productId) => {
        return fetchAPI(`/woo-ai/v1/rag/excluded-products/${productId}`, {
            method: 'DELETE',
        });
    },
};
/**
 * Coupons API Functions
 */
export const couponsApi = {
    /**
     * Get all published coupons
     */
    getAll: async () => {
        return fetchAPI('/woo-ai/v1/coupons');
    },
};
/**
 * Coupon Exclusions API Functions
 */
export const couponExclusionsApi = {
    /**
     * Get list of excluded coupon IDs
     */
    getAll: async () => {
        return fetchAPI('/woo-ai/v1/rag/excluded-coupons');
    },
    /**
     * Add coupons to exclusion list
     */
    add: async (couponIds) => {
        return fetchAPI('/woo-ai/v1/rag/excluded-coupons', {
            method: 'POST',
            body: JSON.stringify({ coupon_ids: couponIds }),
        });
    },
    /**
     * Remove a coupon from exclusion list
     */
    remove: async (couponId) => {
        return fetchAPI(`/woo-ai/v1/rag/excluded-coupons/${couponId}`, {
            method: 'DELETE',
        });
    },
};
